import React, { useState, useEffect } from "react";
import "./Payment.css";
import ConfirmationPage from "../ConfirmationPage";
import { SummaryCard, SummaryRow } from "../SummaryCard";
import { BASE_URL } from "../../helpers/api"; // make sure you have this
import { useNavigate } from "react-router-dom";
import {
  FiArrowLeft, FiCalendar, FiClipboard, FiUser, FiCreditCard, FiMail,
  FiPhone, FiMapPin, FiHome, FiUserCheck, FiAward, FiClock, FiBookmark,
  FiDollarSign, FiCheckCircle,
} from "react-icons/fi";


export default function PaymentForm() {
  const [confirmed, setConfirmed] = useState(false);
  const [appointment, setAppointment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [decidingHospital, setDecidingHospital] = useState(false);
  const [paymentId, setPaymentId] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(null); // "online" | "hospital"
  const navigate = useNavigate();


  const amount = 900;

  // No real gateway is wired in yet, so "PAY NOW" hands off to a local mock
  // gateway page that plays the role of the hosted checkout page a real
  // provider would show. Swapping in a real gateway later means changing
  // this redirect target — the create-order call and everything downstream
  // (verify, webhook, DB status) already matches what a real integration needs.
  const handlePayNow = async () => {
    setPaying(true);
    try {
      const orderRes = await fetch(`${BASE_URL}/payments/create-order`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ appointment_id: appointment.appointment_id }),
      });
      const orderData = await orderRes.json();

      if (!orderRes.ok) {
        alert(orderData.error || "Unable to start payment. Please try again.");
        setPaying(false);
        return;
      }

      navigate(
        `/mock-payment?reference=${encodeURIComponent(orderData.reference)}&amount=${orderData.amount}`
      );
    } catch (err) {
      console.error("Error starting payment:", err);
      alert("Something went wrong while starting the payment.");
      setPaying(false);
    }
  };

  // Patient chooses to skip online payment and pay in person instead.
  // Recorded immediately as "pending" so reception/nursing staff can see it's
  // owed — a staff member marks it collected later (see /payments/mark-paid).
  const handlePayAtHospital = async () => {
    setDecidingHospital(true);
    try {
      const res = await fetch(`${BASE_URL}/payments/pay-at-hospital`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ appointment_id: appointment.appointment_id }),
      });
      const data = await res.json();

      if (!res.ok) {
        alert(data.error || "Unable to record this. Please try again.");
        setDecidingHospital(false);
        return;
      }

      setPaymentMethod("hospital");
      setConfirmed(true);
    } catch (err) {
      console.error("Error recording pay-at-hospital choice:", err);
      alert("Something went wrong. Please try again.");
      setDecidingHospital(false);
    }
  };

  useEffect(() => {
    const fetchAppointment = async () => {
      const appointmentId = localStorage.getItem("appointment_id"); // saved after booking
      if (!appointmentId) {
        setLoading(false);
        return;
      }
      try {
        const res = await fetch(`${BASE_URL}/appointments/${appointmentId}`);
        const data = await res.json();
        console.log("Fetched appointment:", data);
        setAppointment(data);

        // The mock gateway page sends us back here — verify the result
        // server-side before trusting anything about the payment outcome.
        const params = new URLSearchParams(window.location.search);
        const reference = params.get("reference");

        if (data.payment_status === "paid") {
          setPaymentId(data.payment_id);
          setPaymentMethod("online");
          setConfirmed(true);
        } else if (reference) {
          const verifyRes = await fetch(`${BASE_URL}/payments/verify`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ reference }),
          });
          const verifyData = await verifyRes.json();

          if (verifyRes.ok && verifyData.success) {
            setPaymentId(verifyData.payment_id);
            setPaymentMethod("online");
            setConfirmed(true);
          } else {
            alert(verifyData.error || "Payment could not be verified. Please try again.");
          }
          navigate("/payment", { replace: true });
        }
      } catch (err) {
        console.error("Error fetching appointment:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchAppointment();
  }, []);

  if (loading) {
    return <p>Loading appointment...</p>;
  }

  if (!appointment) {
    return <p>No appointment found.</p>;
  }

  if (confirmed) {
    return (
      <ConfirmationPage
        doctorDetails={{
          doctorName: appointment?.doctor?.name || "N/A",
          qualification: appointment?.doctor?.degree?.replace(/<[^>]+>/g, "") || "N/A",
          experience: "12 Years Of Experience", // TODO: add to user table
          specialization: appointment?.doctor?.specialty || "N/A",
          location: appointment?.location?.name || "N/A",
        }}
        patientDetails={{
          name: appointment?.patient?.name || "N/A",
          age: "N/A", // TODO: calculate from patient DOB
          date: appointment?.date || "N/A",
          slotTime: appointment?.time_slot || "N/A",
          type: "NEW", // TODO: detect from appointment history
          email: appointment?.patient?.email || "N/A",
        }}
        amount={amount}
        paymentId={paymentId}
        paymentMethod={paymentMethod}
      />
    );
  }

  const paymentStatusLabel = {
    paid: "Paid Online",
    pay_at_hospital: "Pay at Hospital (Pending)",
    not_paid: "Not Paid",
  }[appointment?.payment_status] || "Not Paid";

  return (
    <div className="payment-container">
      <button className="back-link" onClick={() => navigate(-1)}>
        <FiArrowLeft /> Back to Appointments
      </button>

      <div className="summary-page-header">
        <span className="summary-page-icon"><FiCalendar /></span>
        <div>
          <h2 className="page-title">Appointment Summary</h2>
          <p className="page-subtitle">Here are the details of your scheduled appointment.</p>
        </div>
      </div>

      {/* Booking/identity info first, then clinical/visit info — same
          grouping and field order used on the post-payment confirmation
          screen, so both places show the exact same story. */}
      <SummaryCard icon={<FiClipboard />} title="Appointment Details">
        <SummaryRow icon={<FiUser />} label="Patient Name" value={appointment?.patient?.name} />
        <SummaryRow icon={<FiCreditCard />} label="Patient ID" value={appointment?.patient?.patient_id} />
        <SummaryRow icon={<FiMail />} label="Email" value={appointment?.patient?.email} />
        <SummaryRow icon={<FiPhone />} label="Phone" value={appointment?.patient?.phone} />
        <SummaryRow icon={<FiMapPin />} label="Location" value={appointment?.location?.name} />
        <SummaryRow icon={<FiHome />} label="Department" value={appointment?.doctor?.specialty} />
        <SummaryRow icon={<FiUserCheck />} label="Doctor Name" value={appointment?.doctor?.name} />
        <SummaryRow
          icon={<FiAward />}
          label="Degree"
          value={appointment?.doctor?.degree?.replace(/<[^>]+>/g, "")}
        />
        <SummaryRow icon={<FiCalendar />} label="Appointment Date" value={appointment?.date} />
        <SummaryRow
          icon={<FiClock />}
          label="Appointment Time"
          value={appointment?.time_slot && appointment?.timezone
            ? `${appointment.time_slot} ${appointment.timezone}`
            : appointment?.time_slot}
        />
        {/* This booking flow's "Reason for Visit" field (Routine Checkup /
            New Patient Visit / Specific Concern) is the only visit-type
            selection it captures — shown once here as Appointment Type
            rather than duplicated under a separate "reason" row. */}
        <SummaryRow icon={<FiClipboard />} label="Appointment Type" value={appointment?.problem} />
        <SummaryRow icon={<FiBookmark />} label="Appointment ID" value={appointment?.appointment_id} />
        <SummaryRow icon={<FiDollarSign />} label="Payment Status" value={paymentStatusLabel} />
      </SummaryCard>

      <div className="thank-you-banner">
        <span className="thank-you-icon"><FiCheckCircle /></span>
        <div>
          <strong>Thank you!</strong>
          <p>Your appointment has been successfully scheduled.</p>
        </div>
      </div>

      {/* Fee Details */}
      <div className="summary-card">
        <h3>Fee Details</h3>
        <div className="summary-rows">
          <SummaryRow label="Consultation Fee" value={`₹${amount}`} />
          <SummaryRow label="Total Payable" value={`₹${amount}`} />
        </div>
        {appointment?.payment_status === "pay_at_hospital" && (
          <p style={{ color: "#f37321", fontWeight: 600 }}>
            You chose to pay at the hospital. You can still pay online now if you'd prefer.
          </p>
        )}

        <div className="fee-actions">
       <button className="btn-back" onClick={() => navigate(-1)}>
        BACK
      </button>
          <div style={{ display: "flex", gap: 12 }}>
            {appointment?.payment_status !== "pay_at_hospital" && (
              <button className="btn-back" onClick={handlePayAtHospital} disabled={paying || decidingHospital}>
                {decidingHospital ? "PLEASE WAIT..." : "PAY AT HOSPITAL"}
              </button>
            )}
            <button className="btn-confirm" onClick={handlePayNow} disabled={paying || decidingHospital}>
              {paying ? "PROCESSING..." : "PAY NOW"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
